<script setup lang="ts">
// import SponsorBlock from "@/views/main/components/SponsorBlock.vue"

import { Newspaper } from "lucide-vue-next"
import { useQuery } from "@tanstack/vue-query"
import { useI18n } from "vue-i18n"

import { getLastPublication } from "@/models/publication.ts"

import SloganIt from "@/views/main/HomeView/sloganIt.vue"
import SloganEn from "@/views/main/HomeView/sloganEn.vue"

import IntroBlockIt from "@/views/main/HomeView/introBlockIt.vue"
import IntroBlockEn from "@/views/main/HomeView/introBlockEn.vue"

const { t } = useI18n()

const heroImgSrc: string = "/home.png"

const { data: lastPublications } = useQuery({
  queryKey: ["lastPublications"],
  queryFn: getLastPublication,
  staleTime: 10 * 60 * 1000 // 10 minutes
})

const directors = [
  {
    name: "Teresa M. Gialdroni",
    role: t("home.staff.chief"),
    institution: 'Università degli Studi di Roma "Tor Vergata"'
  },
  {
    name: "Giacomo Sciommeri",
    role: t("home.staff.assistant")
  }
]

const editorialBoard = [
  {
    name: "Annalisa Bini",
    institution: "Accademia Nazionale di Santa Cecilia"
  },
  {
    name: "Massimo Biscardi",
    institution: "Accademia Nazionale di Santa Cecilia"
  },
  {
    name: "Agostino Ziino",
    institution: "University of Rome 'Tor Vergata'"
  }
]

const advisoryBoard = [
  {
    name: "Michele dall'Ongaro",
    institution: "Rossini Opera Festival"
  },
  {
    name: "Luca Aversano",
    institution: "Università di Roma Tre"
  },
  {
    name: "Paola Besutti",
    institution: "Università di Teramo"
  },
  {
    name: "Stefano Campagnolo",
    institution: "Ministero della Cultura, Roma"
  },
  {
    name: "Frederick Hammond",
    institution: "Bard College, Annandale-On-Hudson, NY"
  },
  {
    name: "Margaret Murata",
    institution: "University of California, Irvine"
  },
  {
    name: "Guido Olivieri",
    institution: "University of Texas, Austin"
  },
  {
    name: "Klaus Pietschmann",
    institution: "Johannes Gutenberg-Universität, Mainz"
  },
  {
    name: "Guido Salvetti",
    institution: "Conservatorio “G. Verdi”, Milano"
  },
  {
    name: "Álvaro Torrente",
    institution: "Universidad Complutense, Madrid"
  },
  {
    name: "Lucio Tufano",
    institution: "Università di Palermo"
  },
  {
    name: "Philippe Vendrix",
    institution: "Université François Rabelais, Tours"
  }
]

const selectedLanguage = I18n.locale
</script>

<template>
  <!-- Intro -->
  <section class="body-font pt-12 text-gray-800">
    <div class="container mb-5 flex-row items-center md:flex">
      <div class="w-full md:w-1/3">
        <img class="mx-auto w-full max-w-xs object-center" alt="hero" :src="heroImgSrc" />
      </div>

      <div class="w-full md:w-2/3">
        <p class="mb-8 text-center text-2xl text-primary">
          <slogan-en v-if="selectedLanguage === 'en'" />
          <slogan-it v-else />
        </p>
      </div>
    </div>

    <div class="border-t border-gray-200 bg-gray-100 pb-5 pt-10">
      <h1 class="title-font mb-10 text-center text-3xl font-medium text-gray-900 sm:text-4xl">Studi Musicali</h1>

      <intro-block-en v-if="selectedLanguage === 'en'" />
      <intro-block-it v-else />
    </div>
  </section>

  <!-- publications -->
  <section class="body-font py-12 text-gray-600">
    <div class="container mx-auto px-5">
      <div class="mb-4 flex w-full flex-col text-center">
        <h1 class="title-font mb-2 text-2xl font-medium sm:text-3xl">
          {{ $t("home.publication_title").toUpperCase() }}
        </h1>

        <router-link
          :to="{ name: 'publications_path' }"
          class="ml-auto inline-flex items-center text-primary hover:text-black">
          {{ $t("home.show_publications") }}
          <svg
            class="ml-2 h-4 w-4"
            viewBox="0 0 24 24"
            stroke="currentColor"
            stroke-width="2"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round">
            <path d="M5 12h14"></path>
            <path d="M12 5l7 7-7 7"></path>
          </svg>
        </router-link>
      </div>

      <div class="-m-4 flex flex-wrap">
        <div class="p-4 md:w-1/3" v-for="publication in lastPublications">
          <div class="h-full overflow-hidden rounded-lg border-2 border-gray-200 border-opacity-60 bg-gray-200">
            <img
              class="w-full object-cover object-top md:h-36 lg:h-96"
              :src="`/publications/${publication.id}/cover`"
              alt="blog" />

            <div class="p-6">
              <h1 class="title-font mb-3 text-lg font-medium text-gray-900">
                {{ $t("commons.year") }} {{ publication.year }} - {{ $t("commons.number") }}
                {{ publication.number }}
              </h1>

              <div class="flex flex-wrap items-center">
                <router-link
                  :to="{ name: 'show_publication_path', params: { id: publication.id } }"
                  class="inline-flex items-center text-blue-500 hover:text-primary"
                  >{{ $t("home.go_to_publication") }}
                  <svg
                    class="ml-2 h-4 w-4"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    stroke-width="2"
                    fill="none"
                    stroke-linecap="round"
                    stroke-linejoin="round">
                    <path d="M5 12h14"></path>
                    <path d="M12 5l7 7-7 7"></path>
                  </svg>
                </router-link>

                <span class="ml-auto inline-flex items-center gap-2 text-sm leading-none text-gray-400">
                  <Newspaper class="h-6" />
                  {{ publication.articles_count }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- Staff -->
  <section class="body-font border-t border-gray-200 bg-gray-100 pb-12 pt-12 text-gray-600">
    <div class="container mx-auto px-5">
      <div class="mx-auto mb-8 w-full max-w-md" v-for="director in directors">
        <div class="mb-8 flex w-auto flex-col text-center">
          <h1 class="title-font mb-4 text-2xl font-medium text-gray-900 sm:text-3xl">
            {{ director.role.toUpperCase() }}
          </h1>
        </div>

        <div class="flex items-center rounded-lg border border-gray-300 bg-slate-50 p-4">
          <div class="flex-grow text-center">
            <h2 class="title-font font-medium text-gray-900">{{ director.name }}</h2>
            <p class="text-gray-500">{{ director.institution }}</p>
          </div>
        </div>
      </div>
    </div>

    <div class="container mx-auto px-5">
      <div class="mb-8 flex w-full flex-col text-center">
        <h1 class="title-font mb-4 text-2xl font-medium text-gray-900 sm:text-3xl">
          {{ $t("home.staff.editorial_board") }}
        </h1>
      </div>

      <div class="-m-2 flex flex-wrap">
        <div class="w-full p-2 md:w-1/2 lg:w-1/3" v-for="editor in editorialBoard">
          <div class="flex h-full items-center rounded-lg border border-gray-300 bg-slate-50 p-4">
            <div class="flex-grow text-center">
              <h2 class="title-font font-medium text-gray-900">{{ editor.name }}</h2>
              <p class="text-gray-500">{{ editor.institution }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="container mx-auto px-5">
      <div class="mb-8 mt-8 flex w-full flex-col text-center">
        <h1 class="title-font mb-4 text-2xl font-medium text-gray-900 sm:text-3xl">
          {{ $t("home.staff.advisory_board") }}
        </h1>
      </div>

      <div class="-m-2 flex flex-wrap">
        <div class="w-full p-2 md:w-1/2 lg:w-1/3" v-for="advisor in advisoryBoard">
          <div class="flex h-full items-center rounded-lg border border-gray-300 bg-slate-50 p-4">
            <div class="flex-grow text-center">
              <h2 class="title-font font-medium text-gray-900">{{ advisor.name }}</h2>
              <p class="text-gray-500">{{ advisor.institution }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>

  <!-- partner -->
  <!--  <div class="container py-4">-->
  <!--    <sponsor-block />-->
  <!--  </div>-->
</template>
